import { SiteNavigationElement, WithContext } from 'schema-dts';

import { TopLevelMenuItem } from '~/components/header';
import { env } from '~/lib/atom/env';

const siteURL =
  env.NEXT_PUBLIC_SITE_TYPE === 'B2B' ? env.NEXT_PUBLIC_B2B_URL : env.NEXT_PUBLIC_D2C_URL;

const formatUrl = (baseUrl: string, path: string) => {
  // Ensure base URL doesn't end with slash and path starts with slash
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const formattedPath = path.startsWith('/') ? path : `/${path}`;

  return `${base}${formattedPath}`;
};

export const NavigationSchema = ({ menuItems }: { menuItems: TopLevelMenuItem[] }) => {
  // Create navigation items from your menu structure
  const navigationItems = menuItems.map((item) => {
    // Get all child items from all columns and flatten the structure
    const childItems =
      item.columns?.flatMap((column) => {
        // Handle columns with regular children
        const childrenItems =
          column.children?.map((child) => ({
            '@type': 'SiteNavigationElement' as const,
            name: child.name,
            url: formatUrl(siteURL, child.url),
            // Second level (nested) children if they exist
            ...(child.children && child.children.length > 0
              ? {
                  hasPart: child.children.map((nestedChild) => ({
                    '@type': 'SiteNavigationElement' as const,
                    name: nestedChild.name,
                    url: formatUrl(siteURL, nestedChild.url),
                  })),
                }
              : {}),
          })) || [];

        // Handle columns with imageItem but no children
        const imageItems =
          (!column.children || column.children.length === 0) && column.imageItem?.destinationUrl
            ? [
                {
                  '@type': 'SiteNavigationElement' as const,
                  name: column.imageItem.headingText || column.name || 'Featured',
                  url: formatUrl(siteURL, column.imageItem.destinationUrl),
                },
              ]
            : [];

        // Return both types of items
        return [...childrenItems, ...imageItems];
      }) || [];

    return {
      '@type': 'SiteNavigationElement' as const,
      name: item.name,
      url: formatUrl(siteURL, item.url),
      ...(childItems.length > 0 ? { hasPart: childItems } : {}),
    };
  });

  // Create the schema object
  const schema: WithContext<SiteNavigationElement> = {
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement' as const,
    hasPart: navigationItems,
  };

  return (
    <script
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      type="application/ld+json"
    />
  );
};
